import { Fragment } from "react";
import { Typography } from "@mui/material";
import { OverflowText } from "@verity/components/core";
import { SpreadsheetAnswer, SpreadsheetRowInstructor } from "@verity/api-client/types";
import CorrectIcon from "../../../../../images/quiz-question/option-correct-answer-checked-icon.svg?react";
import InCorrectIcon from "../../../../../images/quiz-question/option-inccorect-icon.svg?react";
import UnansweredStatusIcon from "../../../../../images/quiz-question/question-status-unanswered-icon.svg?react";
import pageStyles from "../QuizResultPanel.module.css";
import styles from "./QuestionAnswer.module.css";
import { AnswerViewProps, CorrectAnswerViewProps, t } from "./shared";

/** Duplicated from take-assessment's getOptionLetter() to avoid a cross-feature import. */
export function getSpreadsheetColumnLetter(index: number): string {
	return String.fromCharCode(65 + index);
}

function isSpreadsheetGradeCell(
	cell: SpreadsheetRowInstructor[number],
): cell is Extract<SpreadsheetRowInstructor[number], { isGradeCell: true }> {
	return "isGradeCell" in cell && !!cell.isGradeCell;
}

export function SpreadsheetAnswerView({ question, response }: AnswerViewProps) {
	const rows = (question.options as SpreadsheetRowInstructor[] | undefined) ?? [];
	const numColumns = rows[0]?.length ?? 0;
	const userRows: SpreadsheetAnswer = (response?.answer as SpreadsheetAnswer) ?? [];
	const isAnswered = response?.isAnswered === true;

	return (
		<div className={styles.spreadsheetTableWrapper}>
			<table className={styles.spreadsheetTable}>
				<thead>
					<tr>
						<th className={styles.spreadsheetCornerCell} />
						{Array.from({ length: numColumns }, (_, c) => (
							<th key={c} className={styles.spreadsheetColHeader}>
								{getSpreadsheetColumnLetter(c)}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row, r) => (
						<tr key={r}>
							<th className={styles.spreadsheetRowHeader}>{r + 1}</th>
							{row.map((cell, c) => {
								if (!isSpreadsheetGradeCell(cell)) {
									return (
										<td key={c} className={styles.spreadsheetCell}>
											<span className={styles.spreadsheetCellContent}>
												<OverflowText
													className={styles.spreadsheetCellText}
													variant="body2"
													fontWeight={400}
													fontSize="14px"
												>
													{cell.text ?? ""}
												</OverflowText>
											</span>
										</td>
									);
								}
								const userText = (userRows[r]?.[c]?.text ?? "").trim();
								const isUnanswered = !isAnswered || !userText;
								const isCorrect = !isUnanswered && userText.toLowerCase() === cell.text.trim().toLowerCase();
								const statusClass = isUnanswered ? "" : isCorrect ? styles.correct : styles.incorrect;
								return (
									<td key={c} className={`${styles.spreadsheetCell} ${styles.spreadsheetAnswerCell} ${statusClass}`}>
										<span className={styles.spreadsheetCellContent}>
											<span className={`${styles.blankInlineChipIcon} ${statusClass}`}>
												{isUnanswered ? (
													<UnansweredStatusIcon style={{ width: "14px" }} />
												) : isCorrect ? (
													<CorrectIcon />
												) : (
													<InCorrectIcon />
												)}
											</span>
											<OverflowText
												className={styles.spreadsheetCellText}
												variant="body2"
												fontWeight={400}
												fontSize="14px"
											>
												{userText || t("unanswered")}
											</OverflowText>
										</span>
									</td>
								);
							})}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export function SpreadsheetCorrectAnswer({ question }: CorrectAnswerViewProps) {
	const rows = (question.options as SpreadsheetRowInstructor[] | undefined) ?? [];
	const cellLabels: { prefix: string; text: string }[] = [];
	rows.forEach((row, r) => {
		row.forEach((cell, c) => {
			if (isSpreadsheetGradeCell(cell)) {
				cellLabels.push({ prefix: `${getSpreadsheetColumnLetter(c)}${r + 1}.`, text: cell.text });
			}
		});
	});
	if (cellLabels.length === 0) return null;
	return (
		<div className={styles.correctAnswerSection}>
			<Typography className={pageStyles.sectionLabel}>{t("correctAnswer")}</Typography>
			<Typography className={styles.correctAnswerItem}>
				{cellLabels.map((label, i) => (
					<Fragment key={i}>
						<span className={styles.correctAnswerLabel}>{label.prefix}</span> {label.text}
						{i < cellLabels.length - 1 ? ", " : ""}
					</Fragment>
				))}
			</Typography>
		</div>
	);
}
